import { ChangeEvent, FC, useMemo, useState, useRef, useEffect } from 'react';
import {
    Box,
    Button,
    Center,
    Checkbox,
    Flex,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    ModalFooter,
    Text,
    useDisclosure,
    Spinner,
    Input,
    VStack,
    IconButton,
    useToast,
    Icon,
    Select,
    Tag,
    TagLeftIcon,
    Alert,
    AlertIcon,
    NumberInputField,
    NumberInput,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    FormControl,
    FormErrorMessage,
    Tooltip,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import { Column, SortByFn, useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';
import FileSaver from 'file-saver';

import { iconTableStyle, linkStyle, tableToolbarStyle } from 'shared/styles';
import GlobalFilter from 'plugins/DITable/filters/GlobalFilter';
import { DITable } from 'plugins/DITable';
import {
    useAddClientKeywordMutation,
    useDeleteClientKeywordsMutation,
    useGetClientKeywordsByTypeQuery,
    useLazyExportClientKeywordsQuery,
    useUpdateClientKeywordsMutation,
} from 'core/API/keywordsApi';
import { useGetRetailerByClientQuery } from 'core/API/authApi';
import { useGetKeywordsLimits, useGetUserInfoSelector } from 'core/redux/selectors/authSelectors';
import { IKeyword, keyable, IVisibleKeyword, keywordAffiliation, IExtendedKeyword, IUserInfo } from 'types';
import TablePagination from 'plugins/DITable/Pagination';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { caseInsensitiveStringsOrder } from 'plugins/DITable/helpers/sort';
import SelectColumnFilter from 'plugins/DITable/filters/SelectFilter';
import { EXCEL_FILE_TYPE } from 'shared/const';
import icons from 'shared/icons';
import { LocaleKey, useTranslation } from 'helpers/languages/translations';
import { isFetchBaseQueryError } from 'utils/erros';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import BooleanColumnFilter from 'plugins/DITable/filters/BooleanColumnFilter';
import AutoCompleteSelect from 'components/atoms/AutoCompleteSelect';
import { EMPTY_KEYWORD, EMPTY_PARTIAL_KEYWORD, KEYWORDS_COLUMNS } from './constants';

const MKeywordsViewer: FC<{ keywordsType: keywordAffiliation; readonly: boolean; userInfo?: IUserInfo }> = ({
    keywordsType,
    readonly,
    userInfo,
}) => {
    const [keywordToAdd, setKeywordToAdd] = useState(keywordsType === 'search' ? EMPTY_KEYWORD : EMPTY_PARTIAL_KEYWORD);
    const [keywordToUpdate, setKeywordToUpdate] = useState<IKeyword | null>(null);
    const [keywordToDelete, setKeywordToDelete] = useState<IKeyword | null>(null);
    const [modalType, setModalType] = useState<'add' | 'update'>('add');
    const [languages, setLanguages] = useState<string[]>([]);
    const [selectedKeywords, setSelectedKeywords] = useState<number[]>([]);
    const [isDeleteAll, setIsDeleteAll] = useState(false);
    const [filters, setFilters] = useState<{ id: string; value: string }[]>([]);
    const [typeOptions, setTypeOptions] = useState<string[]>([]);

    const { data: keywordsData, isError, isLoading } = useGetClientKeywordsByTypeQuery({ type: keywordsType });
    const { data: keywordsOtherTypeData } = useGetClientKeywordsByTypeQuery({
        type: keywordsType === 'banner' ? 'search' : 'banner',
    });
    const [exportKeywords, { isLoading: isExportLoading, isFetching }] = useLazyExportClientKeywordsQuery();
    const [updateKeywords, { isLoading: isUpdateLoading }] = useUpdateClientKeywordsMutation();
    const [deleteKeywords, { isLoading: isDeleteLoading }] = useDeleteClientKeywordsMutation();

    const [addKeyword, { isLoading: isAddLoading }] = useAddClientKeywordMutation();
    const { data: retailers, isLoading: isRetailersLoading, isError: isRetailerError } = useGetRetailerByClientQuery();
    const keywordsLimits = useGetKeywordsLimits();

    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

    const translate = useTranslation();
    const toast = useToast();
    const updateInitialRef = useRef(null);
    const deleteInitialRef = useRef(null);

    useEffect(() => {
        if (!isRetailersLoading && !isRetailerError && retailers) {
            const langs: Set<string> = new Set<string>();
            retailers.data.forEach((retailer) => {
                if (retailer.languages) {
                    retailer.languages.forEach((l: string) => {
                        if (l !== '') langs.add(l);
                    });
                }
            });

            const langsArr = Array.from(langs);
            setLanguages(langsArr);
            setKeywordToAdd({ ...keywordToAdd, language: langsArr[0] });
        }
    }, [retailers, isRetailersLoading]);

    const data: Readonly<any[]> = useMemo(() => {
        if (!isLoading && !isError && keywordsData) {
            const types: Set<string> = new Set<string>();
            keywordsData.keywords.forEach((k) => {
                if (k.type !== '') types.add(k.type);
            });
            setTypeOptions(Array.from(types));

            return keywordsData.keywords;
        }
        return [];
    }, [isLoading, isError, keywordsData]);

    const handleEditKeyword = (keyword: IKeyword) => {
        setModalType('update');
        setKeywordToUpdate(keyword);
        setKeywordToDelete(keyword);
        onOpen();
    };

    const handleOpenDeleteKeyword = () => {
        onClose();
        onDeleteOpen();
    };

    const caseInsensitiveSort = useMemo(() => caseInsensitiveStringsOrder, []);
    const sortTypes: Record<string, SortByFn<any>> = useMemo(
        () => ({
            caseInsensitive: caseInsensitiveSort,
        }),
        [],
    );

    const handleCheckRow = (event: ChangeEvent<HTMLInputElement>, id: number) => {
        if (event.target.checked) setSelectedKeywords([...selectedKeywords, id]);
        else setSelectedKeywords(selectedKeywords.filter((e) => e !== id));
    };

    const handleCheckAllRows = () => {
        if (keywordsData) {
            if (selectedKeywords.length < keywordsData.keywords.length)
                setSelectedKeywords(keywordsData.keywords.map((k) => k.keyword_id));
            else setSelectedKeywords([]);
        }
    };

    const columns: Readonly<Column<any>[]> = useMemo(() => {
        const nbKeywords = keywordsData ? keywordsData.keywords.length : 1;
        const tableColumns: readonly (Column<any> | null)[] = [
            ...(readonly
                ? []
                : [
                      {
                          Header: () => (
                              <Center m={0}>
                                  <Checkbox
                                      onChange={handleCheckAllRows}
                                      isIndeterminate={
                                          selectedKeywords.length > 0 && selectedKeywords.length < nbKeywords
                                      }
                                      isChecked={selectedKeywords.length === nbKeywords}
                                  ></Checkbox>
                              </Center>
                          ),
                          id: 'selector',
                          disableFilters: true,
                          Cell: ({
                              cell: {
                                  row: { original },
                              },
                          }) => {
                              const keywordOriginal = original as IKeyword;
                              return (
                                  <Center>
                                      <Checkbox
                                          onChange={(e) => {
                                              if (!keywordOriginal.read_only)
                                                  handleCheckRow(e, keywordOriginal.keyword_id);
                                          }}
                                          isChecked={selectedKeywords.includes(keywordOriginal.keyword_id)}
                                      ></Checkbox>
                                  </Center>
                              );
                          },
                          width: '5%',
                      } as Column<any>,
                  ]),
            {
                Header: translate('keyword.column.keyword') as string,
                accessor: 'name',
                sortType: 'caseInsensitive',
                width: '20%',
                Cell: ({ cell: { row } }) => {
                    const keywordsRow = row.original as IKeyword;
                    return (
                        <Box
                            onClick={() => {
                                if (!keywordsRow.read_only) {
                                    handleEditKeyword(keywordsRow);
                                }
                            }}
                        >
                            {keywordsRow.read_only ? (
                                <Tooltip label="you can't modify this keyword">
                                    <Text {...linkStyle} cursor="default" color="gray.400">
                                        {row.values.name}
                                    </Text>
                                </Tooltip>
                            ) : (
                                <Text {...linkStyle}>{row.values.name}</Text>
                            )}
                        </Box>
                    );
                },
            },
            {
                Header: translate('keyword.column.type') as string,
                accessor: 'type',
                sortType: 'caseInsensitive',
                Filter: SelectColumnFilter,
                filter: 'equals',
                width: '20%',
            },
            languages.length !== 1
                ? {
                      Header: translate('keyword.column.language') as string,
                      accessor: 'language',
                      Filter: SelectColumnFilter,
                      filter: 'equals',
                      sortType: 'caseInsensitive',
                  }
                : null,
            {
                Header: translate('keyword.column.criticality') as string,
                accessor: 'priority',
            },
            keywordsType === 'search'
                ? {
                      Header: translate('keyword.column.master') as string,
                      accessor: 'master',
                      Filter: BooleanColumnFilter,
                      disableSortBy: true,
                      Cell: ({ cell: { value } }) => <Checkbox isDisabled isChecked={value === 1} />,
                  }
                : null,

            keywordsType === 'search' && userInfo?.is_international === true
                ? {
                      Header: translate('keyword.column.intScorecard') as string,
                      accessor: 'for_int_scorecard',
                      Filter: BooleanColumnFilter,
                      disableSortBy: true,
                      Cell: ({ cell: { value } }) => <Checkbox isDisabled isChecked={value} />,
                  }
                : null,
        ];

        // this is called a type guard
        const filteredColumns = tableColumns.filter((e): e is Column<any> => e !== null);
        return filteredColumns;
    }, [selectedKeywords, keywordsData, languages, userInfo, readonly]);

    const defaultColumn = useMemo(
        () => ({
            Filter: DefaultColumnFilter,
        }),
        [],
    );

    const tableInstance = useTable(
        { columns, data, defaultColumn, sortTypes, initialState: { filters } },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination,
    );

    useEffect(() => {
        setFilters(tableInstance.state.filters);
    }, [tableInstance.state.filters]);

    const handleAddKeywordOpen = () => {
        setModalType('add');
        onOpen();
    };

    const handleCloseAddAndUpdateModal = () => {
        if (modalType === 'add') setKeywordToAdd({ ...EMPTY_KEYWORD, language: languages[0] });
        else setKeywordToUpdate(null);
        onClose();
    };

    const renderTableToolbar = () => {
        const { state, setGlobalFilter } = tableInstance;

        const handleExportKeywords = async (e: any, isDouble = false) => {
            try {
                const { data: res, isError: isExportError } = await exportKeywords({
                    types: isDouble ? ['search', 'banner'] : [keywordsType],
                });
                if (isExportError || !res) throw new Error(translate('keyword.export.error') as string);

                const blob = new Blob([res], {
                    type: EXCEL_FILE_TYPE,
                });
                FileSaver.saveAs(blob, translate('keyword.export.fileName', { type: keywordsType }) as string);
            } catch (error) {
                toast({
                    title: (error as Error).message,
                    status: 'error',
                    isClosable: true,
                });
            }
        };

        const handleDelete = () => {
            setIsDeleteAll(true);
            onDeleteOpen();
        };

        const PlusIcon = icons.plus;
        const DownloadIcon = icons.download;
        const KeyIcon = icons.key;
        return (
            <Flex {...tableToolbarStyle}>
                <GlobalFilter globalFilter={state.globalFilter} setGlobalFilter={setGlobalFilter} />
                <Menu>
                    <MenuButton
                        as={IconButton}
                        {...iconTableStyle}
                        isDisabled={isLoading || isError || (keywordsData && keywordsData.keywords.length === 0)}
                        aria-label="download excel"
                        isLoading={isExportLoading || isFetching}
                        size="sm"
                        icon={<Icon as={DownloadIcon} />}
                    />
                    <MenuList zIndex="modal">
                        <MenuItem onClick={intentionalyFloatingPromiseReturn(handleExportKeywords)}>
                            {translate('keyword.export.singleTypeDownload', { type: keywordsType })}
                        </MenuItem>
                        <MenuItem onClick={intentionalyFloatingPromiseReturn((e) => handleExportKeywords(e, true))}>
                            {translate('keyword.export.doubleTypes')}
                        </MenuItem>
                    </MenuList>
                </Menu>
                <IconButton
                    onClick={handleAddKeywordOpen}
                    {...iconTableStyle}
                    aria-label="add alias"
                    isDisabled={
                        readonly ||
                        isRetailersLoading ||
                        (keywordsData &&
                            keywordsLimits &&
                            keywordsLimits[`${keywordsType}Limit`] &&
                            keywordsData.keywords.length >= keywordsLimits[`${keywordsType}Limit`])
                    }
                    size="sm"
                    icon={<Icon as={PlusIcon} />}
                />

                {keywordsData && keywordsLimits[`${keywordsType}Limit`] && (
                    <Tag
                        size="sm"
                        bgColor="teal.200"
                        border="1px solid"
                        borderColor="teal.300"
                        color="teal.700"
                        mr="8px"
                    >
                        <TagLeftIcon as={KeyIcon} />
                        {translate('page.keyword.masterLimit', {
                            nb: keywordsData.keywords.length,
                            nbMax: keywordsLimits[`${keywordsType}Limit`],
                        })}
                    </Tag>
                )}

                <IconButton
                    size="sm"
                    isDisabled={readonly || selectedKeywords.length === 0}
                    {...iconTableStyle}
                    onClick={handleDelete}
                    aria-label="bulk delete"
                    icon={<Icon as={DeleteIcon} />}
                />
            </Flex>
        );
    };

    const handleChangeUpdateKeyword = (value: string | boolean | number, type: keyof IVisibleKeyword) => {
        /* eslint-disable  @typescript-eslint/no-non-null-assertion */
        switch (type) {
            case 'master':
            case 'for_int_scorecard':
                if (modalType === 'add') setKeywordToAdd({ ...keywordToAdd, [type]: !keywordToAdd[type] });
                else setKeywordToUpdate({ ...keywordToUpdate!, [type]: !keywordToUpdate![type] });
                break;
            default:
                if (modalType === 'add') setKeywordToAdd({ ...keywordToAdd, [type]: value });
                else setKeywordToUpdate({ ...keywordToUpdate!, [type]: value });
        }
    };

    const handleChangeNumberInputKeyword = (val: string, type: keyof IVisibleKeyword) => {
        if (modalType === 'add') setKeywordToAdd({ ...keywordToAdd, [type]: val });
        else setKeywordToUpdate({ ...keywordToUpdate!, [type]: val });
    };

    const renderAttributeByType = (type: keyof IVisibleKeyword, columnName: LocaleKey) => {
        const keywordToUse = modalType === 'add' ? keywordToAdd : keywordToUpdate;
        switch (type) {
            case 'master':
            case 'for_int_scorecard':
                return type === 'for_int_scorecard' && !userInfo?.is_international ? null : (
                    <Flex key={type} minH="40px" w="100%" alignItems="center" fontSize="sm">
                        <Text w="30%">{translate(columnName)}</Text>
                        <Flex flex="1">
                            <Checkbox
                                isDisabled={readonly}
                                onChange={(e) => handleChangeUpdateKeyword(e.target.value, type)}
                                isChecked={Boolean(keywordToUse![type])}
                            ></Checkbox>
                        </Flex>
                    </Flex>
                );
            case 'type':
                return (
                    <Flex key={type} minH="40px" w="100%" alignItems="center" fontSize="sm">
                        <Text w="30%">{translate(columnName)}</Text>
                        <Flex flex="1">
                            <AutoCompleteSelect
                                size="sm"
                                mutable={true}
                                onChange={(value) => handleChangeUpdateKeyword(value as string, type)}
                                value={keywordToUse![type]}
                                addOption={(option) => {
                                    setTypeOptions([...typeOptions, option]);
                                }}
                                readonly={readonly}
                                isClearable={false}
                            >
                                {typeOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </AutoCompleteSelect>
                        </Flex>
                    </Flex>
                );
            case 'language':
                return (
                    <Flex key={type} minH="40px" w="100%" alignItems="center" fontSize="sm">
                        <Text w="30%">{translate(columnName)}</Text>
                        <Flex flex="1">
                            <Select
                                size="sm"
                                onChange={(e) => handleChangeUpdateKeyword(e.target.value, type)}
                                isDisabled={readonly}
                                value={keywordToUse![type]}
                            >
                                {languages.map((l) => (
                                    <option key={l} value={l}>
                                        {l}
                                    </option>
                                ))}
                            </Select>
                        </Flex>
                    </Flex>
                );
            case 'priority':
                return (
                    <Flex key={type} minH="40px" w="100%" alignItems="center" fontSize="sm">
                        <Text w="30%">{translate(columnName)}</Text>
                        <Flex flex="1">
                            <FormControl isInvalid={keywordToUse![type] > 99 || keywordToUse![type] < 0}>
                                <NumberInput
                                    onChange={(v) => handleChangeNumberInputKeyword(v, type)}
                                    value={keywordToUse![type]}
                                    size="sm"
                                    w="100%"
                                    defaultValue={0}
                                    min={1}
                                    max={99}
                                    clampValueOnBlur={false}
                                    isDisabled={readonly}
                                >
                                    <NumberInputField />
                                    <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                    </NumberInputStepper>
                                </NumberInput>
                                {(keywordToUse![type] > 99 || keywordToUse![type] < 0) && (
                                    <FormErrorMessage> {translate('keyword.modal.criticalityError')} </FormErrorMessage>
                                )}
                            </FormControl>
                        </Flex>
                    </Flex>
                );
            default:
                return (
                    <Flex key={type} minH="40px" w="100%" alignItems="center" fontSize="sm">
                        <Text w="30%">{translate(columnName)}</Text>
                        <Flex flex="1">
                            <Input
                                size="sm"
                                onChange={(e) => handleChangeUpdateKeyword(e.target.value, type)}
                                value={keywordToUse![type]}
                                isDisabled={readonly}
                            />
                        </Flex>
                    </Flex>
                );
        }
    };

    const handleUpdateKeywords = async () => {
        try {
            const isNameExists = keywordsData?.keywords.find(
                (k) =>
                    k.name === keywordToUpdate?.name.trim() &&
                    k.language === keywordToUpdate.language &&
                    k.id !== keywordToUpdate.id,
            );

            if (isNameExists)
                throw new Error(
                    translate('keyword.modal.exisitsError', { name: keywordToUpdate!.name.trim() }) as string,
                );
            const checkedKeyword: keyable = { ...keywordToUpdate! };
            Object.entries(checkedKeyword).forEach(([key, val]) => {
                if (typeof val === 'string') {
                    checkedKeyword[key] = val.trim();
                }
            });

            const res = await updateKeywords({ keyword: checkedKeyword as IKeyword, type: keywordsType });

            if ('error' in res) {
                const errorObj = res.error;
                if (isFetchBaseQueryError(errorObj)) {
                    const errorMsg: string =
                        'message' in (errorObj.data as any)
                            ? (errorObj.data as any).message
                            : JSON.stringify(errorObj.data);
                    throw new Error(errorMsg);
                }
                throw new Error(translate('keyword.modal.updateError') as string);
            }

            handleCloseAddAndUpdateModal();
            toast({
                title: translate('keyword.modal.updateSuccesful'),
                status: 'success',
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const handleAddKeyword = async () => {
        try {
            const exisitingKeyword = keywordsData?.keywords.find(
                (k) => k.name === keywordToAdd.name.trim() && k.language === keywordToAdd.language,
            ) as IExtendedKeyword | undefined;

            if (exisitingKeyword && exisitingKeyword?.is_active === true)
                throw new Error(translate('keyword.modal.exisitsError', { name: keywordToAdd.name.trim() }) as string);

            const checkedKeyword: keyable = { ...keywordToAdd };
            Object.entries(checkedKeyword).forEach(([key, val]) => {
                if (typeof val === 'string') {
                    checkedKeyword[key] = val.trim();
                }
            });

            checkedKeyword[`for_${keywordsType}`] = true;

            const res = await addKeyword({
                keyword: checkedKeyword as IVisibleKeyword & {
                    for_banner?: boolean;
                    for_search?: boolean;
                },
                type: keywordsType,
            });

            if ('error' in res) {
                const errorObj = res.error;
                if (isFetchBaseQueryError(errorObj)) {
                    const errorMsg: string =
                        'message' in (errorObj.data as any)
                            ? (errorObj.data as any).message
                            : JSON.stringify(errorObj.data);
                    throw new Error(errorMsg);
                }
                throw new Error(translate('keyword.modal.addError') as string);
            }

            handleCloseAddAndUpdateModal();
            toast({
                title: translate('keyword.modal.addSuccesful'),
                status: 'success',
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const renderUpdateAndAddModal = () => {
        const attributes = KEYWORDS_COLUMNS.filter((c) => c[keywordsType]);
        const keywordToUse = modalType === 'add' ? keywordToAdd : keywordToUpdate;
        const isKeywordInTheOtherType =
            modalType === 'add' &&
            keywordsOtherTypeData?.keywords.some(
                (k) => k.language === keywordToAdd.language && k.name === keywordToAdd.name.trim(),
            );
        return (
            <Modal initialFocusRef={updateInitialRef} isOpen={isOpen} onClose={handleCloseAddAndUpdateModal}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader tabIndex={-1} ref={updateInitialRef}>
                        {translate(modalType === 'update' ? 'keyword.modal.titleUpdate' : 'keyword.modal.titleAdd')}
                    </ModalHeader>
                    <ModalCloseButton mt="2" isDisabled={modalType === 'add' ? isAddLoading : isUpdateLoading} />
                    {modalType === 'update' && keywordToUpdate?.for_banner && keywordToUpdate?.for_search && (
                        <Alert status="info">
                            <AlertIcon />
                            {translate('keyword.modal.updateAlert')}
                        </Alert>
                    )}
                    {isKeywordInTheOtherType && (
                        <Alert status="info">
                            <AlertIcon />
                            {translate('keyword.modal.addAlert')}
                        </Alert>
                    )}
                    <ModalBody>
                        {keywordToUse && (
                            <VStack>{attributes.map((attr) => renderAttributeByType(attr.id, attr.name))}</VStack>
                        )}
                    </ModalBody>
                    <ModalFooter fontSize="sm" justifyContent="space-between">
                        {modalType === 'update' && !readonly && (
                            <Box {...linkStyle} color="red.500" onClick={handleOpenDeleteKeyword}>
                                {translate('keyword.modal.delete')}
                            </Box>
                        )}
                        <Flex flex="1" justifyContent="flex-end">
                            <Button
                                isDisabled={modalType === 'add' ? isAddLoading : isUpdateLoading}
                                size="sm"
                                variant="outline"
                                mr={readonly ? 0 : 3}
                                onClick={handleCloseAddAndUpdateModal}
                            >
                                {translate('keyword.modal.cancel')}
                            </Button>
                            {!readonly && (
                                <Button
                                    onClick={
                                        modalType === 'add'
                                            ? intentionalyFloatingPromiseReturn(handleAddKeyword)
                                            : intentionalyFloatingPromiseReturn(handleUpdateKeywords)
                                    }
                                    isLoading={modalType === 'add' ? isAddLoading : isUpdateLoading}
                                    isDisabled={
                                        modalType === 'add' &&
                                        keywordsData &&
                                        keywordsLimits[`${keywordsType}Limit`] &&
                                        keywordsData.keywords.length >= keywordsLimits[`${keywordsType}Limit`]
                                    }
                                    size="sm"
                                    colorScheme="blue"
                                    bg="blueMain"
                                >
                                    {translate('keyword.modal.apply')}
                                </Button>
                            )}
                        </Flex>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    };

    const renderDeleteModal = () => {
        const handleDeleteKeyword = async () => {
            try {
                const payload = isDeleteAll
                    ? selectedKeywords.map((k) => ({ id: k, type: keywordsType }))
                    : [{ id: keywordToDelete!.keyword_id, type: keywordsType }];
                await deleteKeywords({
                    keywords: payload,
                }).unwrap();

                if (isDeleteAll) setSelectedKeywords([]);
                onDeleteClose();
                toast({
                    title: translate('keyword.modal.deleteSuccesful'),
                    status: 'success',
                    isClosable: true,
                });
            } catch (error) {
                toast({
                    title: translate('keyword.modal.deleteError', { err: (error as Error).message }),
                    status: 'error',
                    isClosable: true,
                });
            }
        };

        const handleCloseDelete = () => {
            setIsDeleteAll(false);
            onDeleteClose();
        };

        return (
            <Modal isCentered initialFocusRef={deleteInitialRef} isOpen={isDeleteOpen} onClose={handleCloseDelete}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader tabIndex={-1} ref={deleteInitialRef}>
                        <Text as="h3">
                            {translate(
                                isDeleteAll ? 'keyword.modal.titleDeleteSingle' : 'keyword.modal.titleDeletePlural',
                            )}
                        </Text>
                    </ModalHeader>
                    <ModalCloseButton mt="2" isDisabled={isDeleteLoading} />
                    <ModalBody fontSize="sm">
                        <Text>{translate('keyword.modal.deleteInfo')}</Text>
                    </ModalBody>

                    <ModalFooter>
                        <Button
                            isDisabled={isDeleteLoading}
                            variant="outline"
                            size="sm"
                            mr={3}
                            onClick={handleCloseDelete}
                        >
                            {translate('keyword.modal.cancel')}
                        </Button>
                        <Button
                            onClick={intentionalyFloatingPromiseReturn(handleDeleteKeyword)}
                            isLoading={isDeleteLoading}
                            colorScheme="red"
                            size="sm"
                        >
                            {translate('keyword.modal.deleteAction')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    };

    return (
        <Box mt="16px">
            {renderTableToolbar()}
            {isLoading || isRetailersLoading ? (
                <Center w="100%" h="200px" bg="white" border="1px solid" borderColor="gray.100">
                    <Spinner />
                </Center>
            ) : (
                <>
                    <DITable {...tableInstance} isPagination isHeaderFixed />
                    <TablePagination tableInstance={tableInstance} />
                </>
            )}
            {renderUpdateAndAddModal()}
            {renderDeleteModal()}
        </Box>
    );
};

export default MKeywordsViewer;
